// API service for centralized API calls
import axios from 'axios';
import { authorizedApi, toApiError, BACKEND_URL } from './authClient';

// Authenticated calls go through `authorizedApi` (see authClient.js): it attaches
// the bearer token, refreshes it before/after expiry, and keeps the HTTP status
// on failures so callers can branch on `err.status === 401` instead of matching
// on the wording of an error message.
//
// The `token` argument these functions used to take is gone. Call sites that
// still pass one are harmless — it was always the trailing parameter, so the
// extra argument is simply ignored.
const API_BASE = `${BACKEND_URL}api/auth`;
const BUSINESS_API_BASE = `${BACKEND_URL}api/business`;
const CLAIMS_API_BASE = `${BACKEND_URL}api/claims`;
const STRIPE_API_BASE = `${BACKEND_URL}api/stripe`;
const USERS_API_BASE = `${BACKEND_URL}api/users`;
const PUBLIC_API_BASE = `${BACKEND_URL}api/public`;

// Unauthenticated instance, for the routes that must work with no session
// (login, signup, OTP, forgot-password).
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
});

// Unwrap Lambda/API Gateway style response (statusCode, headers, body as string)
const unwrapResponse = (res) => {
  if (res && typeof res.body === "string") {
    try {
      return JSON.parse(res.body);
    } catch (_) {
      return res;
    }
  }
  return res;
};

// Auth related API calls
export const authApi = {
  // Forgot password request
  forgotPassword: async (data) => {
    try {
      const response = await api.post("/forgot-password", data);
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Verify OTP
  verifyOTP: async (data) => {
    try {
      const response = await api.post("/otp/verify", data);
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Resend OTP (uses forgot-password endpoint)
  resendOTP: async (data) => {
    try {
      const response = await api.post("/forgot-password", data);
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Change password (for forgot password flow - uses ticket)
  changePassword: async (data) => {
    try {
      const response = await api.patch("/change-password", data);
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Change password for signup flow (uses the session from OTP confirmation)
  changePasswordForSignup: async (data) => {
    try {
      const response = await authorizedApi.patch(
        `${USERS_API_BASE}/change-password`,
        data,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Signup - Send OTP
  signup: async (data) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}api/auth/signup/business-owner`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Confirm OTP and log
  confirmOTP: async (data) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}api/auth/confirm/new`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Resend OTP for signup
  // Goes through the `api` instance rather than a hand-built URL: BACKEND_URL already ends
  // in a slash, so the previous `${BACKEND_URL}/api/auth/...` produced a double slash
  // (…amazonaws.com//api/auth/…).
  resendSignupOTP: async (data) => {
    try {
      const response = await api.post("/resend-otp-signup/new", data);
      return unwrapResponse(response.data);
    } catch (error) {
      throw error.response?.data || { message: error.message };
    }
  },

  // Verify an email with the signup OTP alone — no password. Returns a short-lived,
  // single-use ticket to exchange for a password via changePassword().
  verifyEmail: async (data) => {
    try {
      const response = await api.post("/verify-email", data);
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Login
  login: async (data) => {
    try {
      const response = await axios.post(`${BACKEND_URL}api/auth/login`, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Logout — revoke the refresh token server-side. Bare axios (unauthenticated,
  // like login): the caller may already have a dead access token, and the point
  // is to kill the refresh token, which travels in the body.
  logout: async (data) => {
    try {
      const response = await axios.post(`${BACKEND_URL}api/auth/logout`, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Update user profile
  updateUserProfile: async (data) => {
    try {
      const response = await authorizedApi.patch(
        `${BACKEND_URL}api/users/profile`,
        data,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },
};

export const businessApi = {
  getProfile: async (id) => {
    try {
      const response = await authorizedApi.get(
        `${BUSINESS_API_BASE}?business_id=${id}`,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  getMyProfile: async () => {
    try {
      const response = await authorizedApi.get(`${BUSINESS_API_BASE}/me`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // The dashboard's read: the same profile plus the beer totals (totalPurchased / redeemed /
  // pending). A separate function rather than an option because the totals are opt-in on the
  // backend — they are derived from a scan of every beer row — and every other caller of /me
  // (the admin-shell guard, Payment Info, Profile, ...) shows no count and must not pay for one.
  getMyProfileWithBeerTotals: async () => {
    try {
      const response = await authorizedApi.get(
        `${BUSINESS_API_BASE}/me?include=beerTotals`,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  createProfile: async (data) => {
    try {
      const response = await authorizedApi.post(`${BUSINESS_API_BASE}`, data);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  setPartnerAgreement: async (data) => {
    try {
      const response = await authorizedApi.put(
        `${BUSINESS_API_BASE}/partner_agreement`,
        data,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Record that the signed-in partner accepted the agreement, at the moment they accept.
  // Keyed server-side to their user id (no business exists yet), server-stamped, and
  // idempotent — the durable record of consent. (#28, #29)
  recordAgreementAcceptance: async ({ version }) => {
    try {
      const response = await authorizedApi.post(
        `${BUSINESS_API_BASE}/agreement-acceptance`,
        { agreement_version: version },
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Has the signed-in partner already accepted the current agreement version? Read on
  // return so a partner who accepted and left is not re-prompted, on any device.
  getAgreementAcceptance: async () => {
    try {
      const response = await authorizedApi.get(
        `${BUSINESS_API_BASE}/agreement-acceptance`,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  updateProfile: async (data) => {
    try {
      const response = await authorizedApi.put(`${BUSINESS_API_BASE}`, data);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  setActiveStatus: async (businessId, isActive) => {
    try {
      const response = await authorizedApi.patch(
        `${BUSINESS_API_BASE}/is-active`,
        {
          business_id: businessId,
          is_active: isActive,
        },
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  deleteProfile: async (businessId) => {
    try {
      const response = await authorizedApi.delete(`${BUSINESS_API_BASE}`, {
        data: {
          business_id: businessId,
        },
      });
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },
};

export const claimsApi = {
  claimBeerRecord: async (data) => {
    try {
      const response = await authorizedApi.post(
        `${CLAIMS_API_BASE}/beer-records/claim`,
        data,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  createBeerClaimEntry: async (data) => {
    try {
      const response = await authorizedApi.post(
        `${CLAIMS_API_BASE}/beer-claim-entries`,
        data,
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },
};

export const publicApi = {
  getPurchaseByShortCode: async (shortCode) => {
    try {
      const response = await axios.get(
        `${PUBLIC_API_BASE}/purchases?short-code=${shortCode}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },
};

export const stripeApi = {
  /** POST /api/stripe/account-session — returns { clientSecret, expiresAt, accountId } */
  createAccountSession: async ({ businessId }) => {
    try {
      const response = await authorizedApi.post(
        `${STRIPE_API_BASE}/account-session`,
        { businessId },
      );
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  /** POST /api/stripe/onboardVendor — returns { onboardingUrl, stripeVendorId } */
  onboardVendor: async ({ business_id, return_url, refresh_url }) => {
    try {
      const response = await authorizedApi.post(
        `${STRIPE_API_BASE}/onboardVendor`,
        { business_id, return_url, refresh_url },
      );
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  /** POST /api/stripe/onboardVendor/complete — sync after Stripe redirect */
  completeVendorOnboarding: async ({ business_id }) => {
    try {
      const response = await authorizedApi.post(
        `${STRIPE_API_BASE}/onboardVendor/complete`,
        { business_id },
      );
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  /** GET /api/stripe/payout-methods/default?stripeVendorId=... — returns default payout method details */
  getDefaultPayoutMethod: async ({ stripeVendorId }) => {
    try {
      const response = await authorizedApi.get(
        `${STRIPE_API_BASE}/payout-methods/default?stripeVendorId=${encodeURIComponent(stripeVendorId)}`,
      );
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },
};

export const adminApi = {
  getBusinessListWithStripeStatus: async () => {
    try {
      const response = await authorizedApi.get(`${BUSINESS_API_BASE}/all`);
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },

  // The dashboard list: every business with its beer totals joined on. Opt-in for the same
  // reason as getMyProfileWithBeerTotals — the business profile screen reads the plain list.
  getBusinessListWithBeerTotals: async () => {
    try {
      const response = await authorizedApi.get(
        `${BUSINESS_API_BASE}/all?include=beerTotals`,
      );
      return unwrapResponse(response.data);
    } catch (error) {
      throw toApiError(error);
    }
  },
};

export default api;
